/**
 * HomeV4 — Cyberpunk / Terminal Theme
 * Dark: near-black + neon orange grid + monospace
 * Light: soft slate + subtle orange grid + monospace
 */
import { Link } from '@inertiajs/react';
import { useHomeContent } from '@/lib/homeContent';
import { VideoCard, ChatDemo, VoiceDemo, HomeQuickActions, HomeImageTrends, HomeVideoTrends } from './HomeSections';
import { useIsDark } from '@/hooks/useIsDark';

const CyberBadge = ({ text, dark }) => (
    <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.4em] mb-4"
        style={{ color: dark ? '#ff7b00' : '#c2410c' }}>
        <div className="size-1.5 rounded-full bg-primary animate-pulse" /> {text}
    </div>
);

const isVideoUrl = (url) => /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url || '');

export default function HomeV4({ page, openRegister }) {
    const c = useHomeContent(page?.content);
    const dark = useIsDark();

    const bg = {
        root: dark ? '#060610' : '#f8f7fc',
        alt: dark ? '#08080f' : '#f1f0f9',
        panel: dark ? 'rgba(6,6,16,0.85)' : 'rgba(255,255,255,0.9)',
        grid: dark ? 'rgba(255,123,0,0.025)' : 'rgba(255,123,0,0.04)',
    };
    const text = dark ? 'text-white' : 'text-slate-900';
    const muted = dark ? 'rgba(255,255,255,0.45)' : 'rgba(15,15,30,0.6)';
    const border = dark ? 'rgba(255,123,0,0.2)' : 'rgba(255,123,0,0.25)';
    const accent = dark ? '#ff7b00' : '#c2410c';

    return (
        <div className="min-h-screen" style={{ background: bg.root }}>
            {c.custom_css && <style dangerouslySetInnerHTML={{ __html: c.custom_css }} />}
            {/* Grid overlay */}
            <div className="fixed inset-0 pointer-events-none opacity-60"
                style={{ backgroundImage: `linear-gradient(${bg.grid} 1px,transparent 1px),linear-gradient(to right,${bg.grid} 1px,transparent 1px)`, backgroundSize: '40px 40px' }} />

            {/* ── Hero ─────────────────────────────────────────── */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden">
                {c.hero_bg && (
                    isVideoUrl(c.hero_bg) ? (
                        <video key={c.hero_bg} className="absolute inset-0 h-full w-full object-cover" autoPlay loop muted playsInline style={{ opacity: dark ? 0.12 : 0.16 }}>
                            <source src={c.hero_bg} type="video/mp4" />
                        </video>
                    ) : (
                        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${c.hero_bg})`, opacity: dark ? 0.12 : 0.16 }} />
                    )
                )}
                <div className="absolute top-0 left-[15%] w-px h-full" style={{ background: `linear-gradient(to bottom, transparent, ${accent}40, transparent)` }} />
                <div className="absolute top-[30%] inset-x-0 h-px" style={{ background: `${accent}15` }} />
                <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-24">
                    <div className="border rounded-2xl p-12"
                        style={{ background: bg.panel, backdropFilter: 'blur(20px)', border: `1px solid ${border}`, boxShadow: dark ? `0 0 80px rgba(255,123,0,0.08), inset 0 1px 0 rgba(255,123,0,0.1)` : '0 0 60px rgba(255,123,0,0.06)' }}>
                        <CyberBadge text={c.hero_small_title} dark={dark} />
                        <h1 className={`text-6xl md:text-8xl font-black font-mono leading-tight mb-8 ${text}`}
                            dangerouslySetInnerHTML={{ __html: c.hero_main_title }}
                            style={{ textShadow: `0 0 40px ${accent}25` }} />
                        <p className="font-mono text-lg max-w-2xl mb-12 leading-relaxed" style={{ color: muted }}>
                            <span style={{ color: `${accent}80` }}>// </span>{c.hero_description}
                        </p>
                        <div className="flex gap-4 flex-wrap">
                            {c.hero_btn_1_url ? (
                                <a href={c.hero_btn_1_url}
                                    className="px-10 py-4 font-mono font-bold text-base uppercase tracking-wider hover:scale-105 transition-all text-black"
                                    style={{ background: accent, boxShadow: `0 0 30px ${accent}40` }}>
                                    &gt; {c.hero_btn_1_text}
                                </a>
                            ) : (
                                <button onClick={openRegister}
                                    className="px-10 py-4 font-mono font-bold text-base uppercase tracking-wider hover:scale-105 transition-all text-black"
                                    style={{ background: accent, boxShadow: `0 0 30px ${accent}40` }}>
                                    &gt; {c.hero_btn_1_text}
                                </button>
                            )}
                            <Link href={c.hero_btn_2_url}
                                className="px-10 py-4 font-mono font-bold text-base uppercase tracking-wider hover:scale-105 transition-all border"
                                style={{ color: accent, borderColor: `${accent}30`, background: 'transparent' }}>
                                &gt; {c.hero_btn_2_text}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Stats ─────────────────────────────────────────── */}
            <section className="py-12" style={{ borderTop: `1px solid ${accent}15`, borderBottom: `1px solid ${accent}15`, background: bg.alt }}>
                <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-px"
                    style={{ background: `${accent}12` }}>
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="py-8 px-6 text-center" style={{ background: bg.alt }}>
                            <div className="text-4xl font-black font-mono mb-2"
                                style={{ color: accent, textShadow: `0 0 20px ${accent}40` }}>{c[`stat_number_${i}`]}</div>
                            <div className="text-xs font-mono uppercase tracking-widest" style={{ color: muted }}>{c[`stat_label_${i}`]}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Intro ─────────────────────────────────────────── */}
            {c.show_intro !== false && (
                <section className="py-24" style={{ background: bg.root }}>
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-6 order-2 lg:order-1">
                            <CyberBadge text={c.intro_badge} dark={dark} />
                            <h2 className={`text-4xl md:text-5xl font-black font-mono ${text}`} dangerouslySetInnerHTML={{ __html: c.intro_title }} />
                            <p className="font-mono text-sm leading-relaxed" style={{ color: muted }} dangerouslySetInnerHTML={{ __html: c.intro_desc }} />
                            {[c.intro_check_1, c.intro_check_2].filter(Boolean).map((t, i) => (
                                <div key={i} className="flex items-center gap-2 font-mono text-sm" style={{ color: accent }}>
                                    <span>&gt;</span><span>{t}</span>
                                </div>
                            ))}
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="rounded-2xl overflow-hidden aspect-video"
                                style={{ boxShadow: `0 0 40px ${accent}15`, border: `1px solid ${border}` }}>
                                <video className="w-full h-full object-cover" autoPlay loop muted playsInline>
                                    <source src={c.intro_video} type="video/mp4" />
                                </video>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ── Quick Actions ────────────────────────────────────────── */}
            {c.show_quick_actions !== false && (
                <section className="py-24" style={{ borderTop: `1px solid ${accent}10`, background: bg.alt }}>
                    <div className="max-w-[1400px] mx-auto px-6 space-y-12">
                        <div>
                            <CyberBadge text={c.quick_actions_badge || 'AI Models'} dark={dark} />
                            <h2 className={`text-4xl font-black font-mono ${text}`} dangerouslySetInnerHTML={{ __html: c.quick_actions_title || 'Explore AI Capability' }} />
                            <p className="font-mono text-sm mt-2" style={{ color: muted }}>{c.quick_actions_desc || 'Access all premium generation models and tools instantly from your dashboard.'}</p>
                        </div>
                        <HomeQuickActions />
                    </div>
                </section>
            )}

            {/* ── Image Trends ──────────────────────────────────────── */}
            {c.show_trends !== false && (
                <section className="py-24" style={{ borderTop: `1px solid ${accent}10`, background: bg.alt }}>
                    <div className="max-w-[1400px] mx-auto px-6 space-y-12">
                        <div>
                            <CyberBadge text={c.trends_badge || 'Trending'} dark={dark} />
                            <h2 className={`text-4xl font-black font-mono ${text}`} dangerouslySetInnerHTML={{ __html: c.trends_title || 'Discover AI Image Trends' }} />
                            <p className="font-mono text-sm mt-2" style={{ color: muted }}>{c.trends_desc || 'Explore our most popular AI templates and creative presets.'}</p>
                        </div>
                        <HomeImageTrends />
                    </div>
                </section>
            )}

            {/* ── Image Studio ──────────────────────────────────── */}
            {c.show_images !== false && (
                <section className="py-24" style={{ borderTop: `1px solid ${accent}10`, background: bg.alt }}>
                    <div className="max-w-[1200px] mx-auto px-6 space-y-12">
                        <div>
                            <CyberBadge text={c.image_studio_badge} dark={dark} />
                            <h2 className={`text-4xl font-black font-mono ${text}`} dangerouslySetInnerHTML={{ __html: c.image_studio_title }} />
                            <p className="font-mono text-sm mt-2" style={{ color: muted }}>{c.image_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {c.image_cards.map((item, i) => (
                                <div key={i} className="aspect-[3/4] overflow-hidden group relative cursor-pointer transition-all"
                                    style={{ border: `1px solid ${border}` }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.boxShadow = `0 0 20px ${accent}20`; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.boxShadow = 'none'; }}>
                                    <div className="w-full h-full bg-cover bg-center group-hover:scale-110 transition-transform duration-700" style={{ backgroundImage: `url('${item.main}')` }} />
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center group-hover:bg-transparent transition-colors duration-500">
                                        <h3 className="font-mono font-bold uppercase tracking-widest group-hover:opacity-0 transition-opacity" style={{ color: accent }}>{item.title}</h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Video Trends ──────────────────────────────────── */}
            {c.show_video_trends !== false && (
                <section className="py-24" style={{ borderTop: `1px solid ${accent}10`, background: bg.root }}>
                    <div className="max-w-[1400px] mx-auto px-6 space-y-12">
                        <div>
                            <CyberBadge text={c.video_trends_badge || 'Video Trends'} dark={dark} />
                            <h2 className={`text-4xl font-black font-mono ${text}`} dangerouslySetInnerHTML={{ __html: c.video_trends_title || 'Trending Avatar Templates' }} />
                            <p className="font-mono text-sm mt-2" style={{ color: muted }}>{c.video_trends_desc || 'Preview popular avatar video templates before creating your own.'}</p>
                        </div>
                        <HomeVideoTrends />
                    </div>
                </section>
            )}

            {/* ── Video Studio ──────────────────────────────────── */}
            {c.show_videos !== false && (
                <section className="py-24" style={{ borderTop: `1px solid ${accent}10`, background: bg.root }}>
                    <div className="max-w-[1200px] mx-auto px-6 space-y-12">
                        <div>
                            <CyberBadge text={c.video_studio_badge} dark={dark} />
                            <h2 className={`text-4xl font-black font-mono ${text}`} dangerouslySetInnerHTML={{ __html: c.video_studio_title }} />
                            <p className="font-mono text-sm mt-2" style={{ color: muted }}>{c.video_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {c.video_cards.map((item, i) => <VideoCard key={i} item={item} cardClass="border-0" />)}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Chat ──────────────────────────────────────────── */}
            {c.show_chat !== false && (
                <section className="py-24" style={{ borderTop: `1px solid ${accent}10`, background: bg.alt }}>
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-6 order-2 lg:order-1">
                            <CyberBadge text={c.chat_badge} dark={dark} />
                            <h2 className={`text-4xl font-black font-mono ${text}`} dangerouslySetInnerHTML={{ __html: c.chat_title }} />
                            <p className="font-mono text-sm leading-relaxed" style={{ color: muted }}>{c.chat_desc}</p>
                            <ul className="space-y-2">
                                {c.chat_features.map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 font-mono text-sm" style={{ color: muted }}>
                                        <span style={{ color: accent }}>&gt;</span>{f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="order-1 lg:order-2" style={{ border: `1px solid ${border}` }}>
                            <ChatDemo panelClass="border-0 rounded-none" style={{ background: bg.panel }} />
                        </div>
                    </div>
                </section>
            )}

            {/* ── Voice ─────────────────────────────────────────── */}
            {c.show_voice !== false && (
                <section className="py-24" style={{ borderTop: `1px solid ${accent}10`, background: bg.root }}>
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div style={{ border: `1px solid ${border}` }}>
                            <VoiceDemo panelClass="border-0 rounded-none" style={{ background: bg.panel }} />
                        </div>
                        <div className="space-y-6">
                            <CyberBadge text={c.voice_badge} dark={dark} />
                            <h2 className={`text-4xl font-black font-mono ${text}`} dangerouslySetInnerHTML={{ __html: c.voice_title }} />
                            <p className="font-mono text-sm leading-relaxed" style={{ color: muted }}>{c.voice_desc}</p>
                            <ul className="space-y-2">
                                {c.voice_features.map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 font-mono text-sm" style={{ color: muted }}>
                                        <span style={{ color: accent }}>&gt;</span>{f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            )}

            {/* ── CTA ───────────────────────────────────────────── */}
            <section className="py-32 text-center relative" style={{ borderTop: `1px solid ${accent}15`, background: bg.alt }}>
                <div className="absolute inset-0" style={{ background: `radial-gradient(circle at center,${accent}08 0%,transparent 70%)` }} />
                <div className="relative z-10 max-w-[700px] mx-auto px-6">
                    <h2 className={`text-6xl font-black font-mono ${text} mb-6`} style={{ textShadow: `0 0 40px ${accent}25` }}>
                        {c.cta_title}<span style={{ color: accent }}>_{c.cta_subtitle}</span>?
                    </h2>
                    <p className="font-mono text-lg mb-12" style={{ color: muted }}>{c.cta_desc}</p>
                    <button onClick={openRegister}
                        className="px-16 py-6 font-mono font-black text-xl uppercase tracking-wider hover:scale-105 transition-all text-black"
                        style={{ background: accent, boxShadow: `0 0 60px ${accent}40` }}>
                        &gt; {c.cta_btn_primary}
                    </button>
                    <p className="font-mono text-xs mt-6" style={{ color: muted }}>{c.cta_tagline.replace(/·/g, '//')}</p>
                </div>
            </section>
        </div>
    );
}
